import React, { useState, useCallback, useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import {
  useDataChannel,
  useLocalParticipant,
  useTrackToggle,
  useRoomContext,
  useRemoteParticipants,
} from "@livekit/components-react";
import { useGlobalState } from "@/hooks/useGlobalState";
import { useMessages } from "@/hooks";
import APP_EVENTS from "@/configs/events";
import { get } from "@/utils/helpers";
import { setValue } from "@/utils/storage";
import { captureError, logger } from "@/utils/logger";
import { ChatHeader } from "./components/ChatHeader";
import { ChatFooterVoice } from "./components/ChatFooterVoice";
import { useOnTranscriptions } from "./hooks/use-on-transcriptions";
import { useAgentTimeout } from "./providers/AgentTimeoutProvider";
import { ReconnectPrompt } from "./components/ReconnectPrompt";
import { VoiceMode } from "./components/VoiceMode";
import { CartProduct, Product } from "./types/product";
import { productToCartProduct } from "./utils/product-to-cart-product";
import { ConnectionState, Track } from "livekit-client";
import TextMode from "./components/TextMode";
import {
  AGENT_CONNECTION_TIMEOUT,
  CHAT_WINDOW_MAX_HEIGHT,
  CHAT_WINDOW_TOP_MARGIN,
} from "./configs/constants";
import { useLivekitRpc } from "./hooks/use-livekit-rpc";
import { useShadowRoot } from "./hooks/use-shadow-root";
import { useMediaQuery } from "@/hooks/use-media-query";
import {
  isNowutalkAgentParticipant,
  useCompatibleVoiceAssistant,
} from "@/hooks/use-compatible-voice-assistant";
import { SHOPPING_ASSISTANT_GREETING } from "@/configs/greeting";
import { loadStorefrontCatalog } from "@/utils/shopify-storefront-catalog";

interface Step2Props {
  setStep: (step: number) => void;
}

const Step2: React.FC<Step2Props> = ({ setStep }) => {
  const shadowRoot = useShadowRoot();
  const {
    chatMode,
    isMicrophoneEnabled,
    setIsMicrophoneEnabled,
    showReconnectPrompt,
    micPermission,
    setShowReconnectPrompt,
    reconnect,
    config,
  } = useGlobalState();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const bottomMargin = isMobile
    ? config?.widget?.margins?.mobileBottom || 12
    : config?.widget?.margins?.bottom || 44;
  const maxHeight = `min(calc(100vh - ${bottomMargin + CHAT_WINDOW_TOP_MARGIN}px), ${CHAT_WINDOW_MAX_HEIGHT}px)`;
  const room = useRoomContext();
  const { enabled: trackEnabled, track: localTrack } = useTrackToggle({
    source: Track.Source.Microphone,
  });
  const { localParticipant } = useLocalParticipant();
  const { handleUserAction, handleAgentAction } = useAgentTimeout();
  const [cartItems, setCartItems] = useState<CartProduct[]>([]);
  const pendingCartProducts = useRef(new Map<number, Product>());
  const [triggerScrollToBottomCounter, setTriggerScrollToBottomCounter] =
    useState<number>(0);
  const incrementTriggerScrollToBottomCounter = useCallback(() => {
    setTriggerScrollToBottomCounter((prev) => prev + 1);
  }, [setTriggerScrollToBottomCounter]);
  const { messages, sendChat, addAgentMessage } = useMessages();
  const [productList, setProductList] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const voiceGreetingRequested = useRef(false);
  const catalogSnapshotSentTo = useRef<string | null>(null);
  const remoteParticipants = useRemoteParticipants();
  const agent = useCompatibleVoiceAssistant();
  const sendRpc = useLivekitRpc(room);
  const isLocalTrackPublished =
    !!localTrack?.track && !localTrack.track.isMuted;

  const handleSendChat = useCallback(
    (message: string) => {
      sendChat(message);
    },
    [sendChat],
  );

  useEffect(() => {
    addAgentMessage({
      id: "nowutalk-opening-greeting",
      text: SHOPPING_ASSISTANT_GREETING,
      timestamp: Date.now(),
    });
  }, [addAgentMessage]);

  useEffect(() => {
    const agentIdentity = agent.agent?.identity;
    if (!agentIdentity || catalogSnapshotSentTo.current === agentIdentity) {
      return;
    }

    let cancelled = false;
    const syncCatalog = async () => {
      try {
        const storefrontProducts = await loadStorefrontCatalog();
        const products = storefrontProducts.slice(0, 250).map((product) => ({
          id: product.id,
          title: product.title,
          handle: product.handle,
          vendor: product.vendor,
          product_type: product.product_type,
          tags: Array.isArray(product.tags)
            ? product.tags.slice(0, 20)
            : String(product.tags || "").slice(0, 600),
          body_html: String(product.body_html || "")
            .replace(/<[^>]*>/g, " ")
            .replace(/\s+/g, " ")
            .trim()
            .slice(0, 1800),
          options: (product.options || []).slice(0, 6),
          images: (product.images || []).slice(0, 2),
          variants: (product.variants || []).slice(0, 6),
        }));
        // LiveKit RPC payloads are intentionally small. Three enriched products
        // stay comfortably below the limit even with long Shopify CDN URLs.
        const chunkSize = 3;

        for (let index = 0; index < products.length; index += chunkSize) {
          if (cancelled) return;
          await sendRpc("catalog_snapshot", {
            products: products.slice(index, index + chunkSize),
            reset: index === 0,
            complete: index + chunkSize >= products.length,
          });
        }

        if (!cancelled) {
          catalogSnapshotSentTo.current = agentIdentity;
          logger.log("Synced live Shopify catalog to the sales agent", {
            products: products.length,
          });
        }
      } catch (error) {
        captureError("Failed to synchronize the live Shopify catalog", error);
      }
    };

    syncCatalog();
    return () => {
      cancelled = true;
    };
  }, [agent.agent?.identity, sendRpc]);

  const selectProduct = useCallback(
    (product: Product | null) => {
      setSelectedProduct(product);
      sendRpc("user_context", { id: product?.id });
    },
    [setSelectedProduct, sendRpc],
  );
  const handleSelectProduct = (product: Product | null) => {
    try {
      logger.log("Product selected:", product);
      selectProduct(product);
    } catch (error) {
      logger.error("RPC call failed", error);
      Sentry.captureException(error);
    }
  };

  useEffect(() => {
    if (room?.state === ConnectionState.Connected) {
      const timeout = setTimeout(() => {
        const remoteAgent =
          remoteParticipants.find(isNowutalkAgentParticipant) ??
          remoteParticipants[0];

        if (!remoteAgent || !remoteAgent.identity) {
          reconnect({
            newRoom: true,
          });
        }
      }, AGENT_CONNECTION_TIMEOUT);

      return () => clearTimeout(timeout);
    }
  }, [room?.state, room, remoteParticipants]);

  useEffect(() => {
    async function handleChatMode() {
      if (chatMode === "voice") {
        window.localStorage.setItem("chatMode", "voice");

        if (micPermission === "granted") {
          setIsMicrophoneEnabled(true);
        } else {
          setIsMicrophoneEnabled(false);
        }

        // Unmute agent voice
        const audio = shadowRoot?.querySelectorAll(`audio`);
        if (audio && audio.length > 0) {
          (audio[0] as HTMLAudioElement).muted = false;
        }

        if (agent.agent?.identity && !voiceGreetingRequested.current) {
          voiceGreetingRequested.current = true;
          sendRpc("switch_mode", { audio_enable: true }).catch((error) => {
            voiceGreetingRequested.current = false;
            captureError("Failed to start the voice greeting", error);
          });
        }
      } else if (chatMode === "text") {
        voiceGreetingRequested.current = false;
        window.localStorage.setItem("chatMode", "text");
        setIsMicrophoneEnabled(false);

        if (agent.agent?.identity) {
          sendRpc("switch_mode", { audio_enable: false }).catch((error) => {
            captureError("Failed to synchronize text mode", error);
          });
        }

        // Mute agent voice
        const audio = shadowRoot?.querySelectorAll(`audio`);
        if (audio && audio.length > 0) {
          (audio[0] as HTMLAudioElement).muted = true;
        }

        if (localTrack?.track) {
          const mediaStreamsTracks = localTrack.track.mediaStream?.getTracks();
          mediaStreamsTracks?.forEach((track) => {
            track.stop();
            localParticipant.unpublishTrack(track);
          });
        }
      }
    }

    handleChatMode();
  }, [
    agent.agent?.identity,
    chatMode,
    localParticipant,
    localTrack,
    micPermission,
    sendRpc,
    setIsMicrophoneEnabled,
    shadowRoot,
  ]);
  //
  // Sync global state and track state
  useEffect(() => {
    if (room?.state === ConnectionState.Connected) {
      if (isMicrophoneEnabled !== trackEnabled) {
        localParticipant
          .setMicrophoneEnabled(isMicrophoneEnabled)
          .catch((error) => {
            setIsMicrophoneEnabled(false);
            captureError("Failed to synchronize microphone state", error);
          });
      }
    }
  }, [
    isMicrophoneEnabled,
    trackEnabled,
    room?.state,
    localParticipant,
    setIsMicrophoneEnabled,
  ]);

  useOnTranscriptions((transcriptions, participant) => {
    if (participant?.identity.startsWith("agent")) {
      handleAgentAction();
      setShowReconnectPrompt(false);
    } else if (participant?.sid === localParticipant.sid) {
      setValue("last_interaction", Date.now().toString());
      handleUserAction();
    }
  });

  const handleBasketClick = useCallback(() => {
    const currentUrl = window.location.origin;
    window.location.href = `${currentUrl}/cart`;
  }, []);

  // loading data from room/channel
  const handleDataMessage = useCallback(
    (decoded: any) => {
      logger.log("Received data from LK:", decoded);
      const eventType = get(decoded, "event_type");

      // Handle product_listings event - sets the product list
      if (eventType === APP_EVENTS.PRODUCT_LISTINGS) {
        const decodedProducts = (decoded?.data?.products || []).map(
          (item: Product) => ({
            ...item,
            isProduct: true,
            timestamp: Date.now(),
          }),
        );

        setSelectedProduct(null);
        setProductList(decodedProducts);

        // Create random id of 4 numbers
        const randomId = Math.floor(1000 + Math.random() * 9000);

        setValue(`products_${randomId}`, JSON.stringify(decodedProducts));
        sendChat(`products_${randomId}`);
      }
      // Typed sessions receive assistant replies directly over LiveKit data.
      // This keeps text chat independent from audio-track/TTS availability.
      else if (eventType === APP_EVENTS.AGENT_MESSAGE) {
        const agentText = String(decoded?.data?.text || "");
        if (chatMode === "text") {
          addAgentMessage({ ...decoded?.data, text: agentText });
        }
        handleAgentAction();
        setShowReconnectPrompt(false);
        incrementTriggerScrollToBottomCounter();
      }
      // Handle add_to_cart event - adds product to cart
      else if (eventType === APP_EVENTS.ADD_TO_CART && get(decoded, "data")) {
        const { data } = decoded;
        const { variant_id, product } = data;
        console.log("data: added to cart:", { variant_id, product, data });

        if (product && product.variants) {
          const reorderedVariants = [...product.variants].sort(
            (a: any, b: any) => {
              if (get(a, "id", "") === variant_id) return -1;
              if (get(b, "id", "") === variant_id) return 1;
              return 0;
            },
          );

          const updatedProduct = { ...product, variants: reorderedVariants };
          setProductList([updatedProduct]);
        }

        const cartProduct = productToCartProduct(data.product);
        setCartItems((prev) => [...prev, cartProduct]);
      }
      // Handle show_product event - shows a specific product
      else if (eventType === APP_EVENTS.SHOW_PRODUCT) {
        logger.log(APP_EVENTS.SHOW_PRODUCT, decoded?.data);
        // If product data is provided, select it
        if (decoded?.data?.product_id) {
          // Product ID is provided, might need to find product from list
          // or wait for product_description event
        }
      }
      // Handle product_description event - shows product description/details
      else if (eventType === APP_EVENTS.PRODUCT_SHOW_DESCRIPTION) {
        Sentry.captureMessage(APP_EVENTS.PRODUCT_SHOW_DESCRIPTION);
        logger.log(APP_EVENTS.PRODUCT_SHOW_DESCRIPTION, decoded?.data);
        selectProduct(decoded?.data.product);
      }
      // Handle go_to_page event - navigation
      else if (eventType === APP_EVENTS.GO_TO_PAGE) {
        const url = decoded?.data?.url;
        if (url) {
          logger.log(APP_EVENTS.GO_TO_PAGE, { url });
          // Navigation is handled by parent window via postMessage
        }
      }

      // Always send event to parent window
      const messageType = eventType || "VOICE_COMMAND_RECEIVED";
      const eventToParent = {
        type: messageType,
        data: {
          event_type: messageType,
          products: decoded?.data?.products,
          variant_id: decoded?.data?.variant_id,
          quantity: decoded?.data?.quantity,
          error: decoded?.error,
          url: decoded?.data?.url,
        },
      };

      window.postMessage(eventToParent, window.location.origin);
    },
    [
      addAgentMessage,
      chatMode,
      handleAgentAction,
      incrementTriggerScrollToBottomCounter,
      sendChat,
      selectProduct,
      setShowReconnectPrompt,
    ],
  );

  const onDataReceived = useCallback(
    (msg: { topic: string; payload: ArrayBuffer }) => {
      const decoded = JSON.parse(new TextDecoder("utf-8").decode(msg.payload));

      if (msg.topic === "transcription") {
        console.log("transcription", decoded);
      } else {
        handleDataMessage(decoded);
      }
    },
    [handleDataMessage],
  );

  useDataChannel(onDataReceived);

  useEffect(() => {
    const onStorefrontMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data?.data;
      if (data?.event_type !== "add_to_cart_succeeded") return;

      const variantId = Number(data.variant_id);
      const product = pendingCartProducts.current.get(variantId);
      if (!product) return;

      const cartProduct = productToCartProduct(product);
      setCartItems((current) => {
        if (current.some((item) => item.variant_id === variantId))
          return current;
        return [...current, cartProduct];
      });
      pendingCartProducts.current.delete(variantId);
    };

    window.addEventListener("message", onStorefrontMessage);
    return () => window.removeEventListener("message", onStorefrontMessage);
  }, []);

  const handleAddToCart = useCallback((product: Product | null) => {
    if (!product || !product.variants || product.variants.length === 0) {
      captureError("No product or variants available to add to cart.", {
        product,
      });

      return;
    }

    const variant =
      product.variants.find((item) => item.id === product.primary_variant_id) ||
      product.variants.find((item) => item.available_for_sale) ||
      product.variants[0];
    pendingCartProducts.current.set(Number(variant.id), product);
    window.postMessage(
      {
        type: "NOWUTALK_CART_ACTION",
        data: {
          event_type: "add_to_cart_confirmed",
          variant_id: variant.id,
          quantity: 1,
        },
      },
      window.location.origin,
    );
    logger.log("Confirmed Shopify cart action", {
      productId: product.id,
      variantId: variant.id,
    });
  }, []);

  const handleClose = useCallback(() => {
    const audio = shadowRoot?.querySelectorAll(`audio`);
    if (audio && audio.length > 0) {
      (audio[0] as HTMLAudioElement).muted = true;
    }

    setTimeout(() => {
      setStep(0);
    }, 300);
  }, [setStep]);

  return (
    <div
      className={`nut-w-full md:nut-w-[400px]
        nut-bg-white nut-rounded-[24px] nut-border nut-border-gray-200
        nut-flex nut-flex-col nut-overflow-hidden nut-p-2 md:nut-p-[20px] 
        ${
          agent.state === "speaking" && chatMode === "voice"
            ? "voice-chat-default"
            : ""
        }`}
      style={{
        height: maxHeight,
        maxHeight: maxHeight,
      }}
    >
      <ChatHeader
        onClose={handleClose}
        agentState={agent.state}
        isMicrophoneEnabled={isMicrophoneEnabled}
      />
      {chatMode === "text" && (
        <>
          <TextMode
            onAddToCart={handleAddToCart}
            onSelectProduct={handleSelectProduct}
            selectedProduct={selectedProduct}
            cartItems={cartItems}
            messages={messages}
            triggerScrollToBottomCounter={triggerScrollToBottomCounter}
            showReconnectPrompt={showReconnectPrompt}
            sendChat={handleSendChat}
            handleBasketClick={handleBasketClick}
          />
        </>
      )}
      {chatMode === "voice" && (
        <>
          <VoiceMode
            messages={messages}
            agentState={agent.state}
            cartItems={cartItems}
            selectedProduct={selectedProduct}
            onSelectProduct={handleSelectProduct}
            productList={productList}
            onAddToCart={handleAddToCart}
            onCheckout={handleBasketClick}
          />
          {showReconnectPrompt && <ReconnectPrompt />}
          <ChatFooterVoice
            changeModeDisabled={isMicrophoneEnabled && !isLocalTrackPublished}
            onShoppingClick={handleBasketClick}
            cartItems={cartItems.length}
          />
        </>
      )}
    </div>
  );
};

export default Step2;
